import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { userService } from "services/userService";
import { RootState } from "store";
import { login } from "store/apps/auth";
import { UserUpdateInfo } from "types/user";
import { errorAlert, successAlert } from "utils/alertHandler";

export default function useProfileEdit() {
  const { user } = useSelector((state: RootState) => state.auth);

  const { t } = useTranslation();

  const [firstName, setFirstName] = useState<string>(user!["first_name"]);
  const [lastName, setLastName] = useState<string>(user!["last_name"]);
  const [email, setEmail] = useState<string>(user!["contact_email"] || "");
  const [phone, setPhone] = useState<number | null>(user!["contact_phone"] || null);
  const [image, setImage] = useState<string>(user!["personal_image_url"] || "");
  const [instagram, setInstagram] = useState<string>(user!["instagram_id"] || "");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isOpenTerms, setIsOpenTerms] = useState<boolean>(false);

  const dispatch = useDispatch();

  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [missingFields, setMissingFields] = useState<string[]>([]);

  const fields = {
    firstName: firstName,
    lastName: lastName,
    email: email,
    phone: phone,
    instagram: instagram,
  };

  const fieldNames = {
    firstName: t("profile_update_first_name", "First Name"),
    lastName: t("profile_update_last_name", "Last Name"),
    email: t("profile_update_email", "Email"),
    phone: t("profile_update_phone", "Phone"),
    instagram: t("field_instagram", "Instagram"),
  };

  useEffect(() => {
    const emptyFields = Object.keys(fields).filter((key: any) => {
      const value = fields[key as keyof typeof fields];
      return typeof value === "string" ? value.trim() === "" : value === null || value === undefined;
    });

    setMissingFields(emptyFields.map((key) => fieldNames[key as keyof typeof fieldNames]));
    setIsFormValid(emptyFields.length === 0);
  }, [firstName, lastName, email, phone, instagram]);

  const _onUserImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e.target.files) return;

      setImageFile(e.target.files[0]);
      setImage(URL.createObjectURL(e.target.files[0]));
    } catch (error: any) {
      errorAlert(error?.message);
    }
  };

  const _onSubmit = async () => {
    try {
      setIsUploading(true);

      const data: UserUpdateInfo = {
        last_name: firstName,
        first_name: lastName,
        contact_phone: phone,
        contact_email: email,
        contact_phone_prefix: "852",
        instagram_id: instagram,
      };

      const result = await userService.updateUserInfo(data, imageFile);

      setIsUploading(false);

      if (!result) return;

      successAlert(t("profile_update_success") + "!");

      dispatch(login(result));
    } catch (error: any) {
      errorAlert(error?.message);
      setIsUploading(false);
    }
  };

  const _onOpen = (): void => {
    setIsShowModal(true);
  };

  const _onClose = (): void => {
    if (isOpenTerms) {
      setIsOpenTerms(false);
      return;
    }

    setIsShowModal(false);
  };

  const _onOpenTerms = (): void => {
    setIsOpenTerms((prev) => !prev);
  };

  return {
    firstName,
    lastName,
    email,
    phone,
    instagram,
    image,
    isUploading,
    isShowModal,
    isFormValid,
    isOpenTerms,
    missingFields,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setInstagram,
    _onUserImageUpload,
    _onOpen,
    _onOpenTerms,
    _onClose,
    _onSubmit,
  };
}
